import { Deprecated, Nullable } from '../../foundation'

/* eslint-disable @typescript-eslint/no-explicit-any */
export type Country = 'KR' | 'JP'
export type SupportedLanguage = 'ko' | 'ja'
export type OperationType = 'SURGICAL' | 'NONSURGICAL' | 'UNKNOWN'
export type OriginType = 'EVENT' | 'HOSPITAL' | 'TENDER'

export enum ConsultationState {
  /** 신규상담 */
  'NONE' = 'NONE',
  /** 내원예정 */
  'FIXED' = 'FIXED',
  /** 전화/채팅완료 */
  'CALLED' = 'CALLED',
  /** 재전화 필요 */
  'RECALL' = 'RECALL',
  /** 통화실패 */
  'NOTANSWER' = 'NOTANSWER',
  /** 내원 안함 */
  'GONE' = 'GONE',
  /** 불량DB처리 */
  'CANCELED' = 'CANCELED',
  /** 내원 완료 */
  'VISITED' = 'VISITED',
  /** 시술 완료 */
  'OPERATED' = 'OPERATED',
  /** 시술 예정 */
  'OPERATION_BOOKED' = 'OPERATION_BOOKED',
  /** 시술 예약 취소 */
  'OPERATION_CANCELED' = 'OPERATION_CANCELED',
  /** 한달 후 */
  'FINISHED' = 'FINISHED',
  /** 확정대기 */
  'NOT_CONFIRMED' = 'NOT_CONFIRMED',
  /** 내원 예약 취소 */
  'VISIT_CANCELED' = 'VISIT_CANCELED',
  /** 수술 안함 */
  'VISITED_AND_GONE' = 'VISITED_AND_GONE',
}

export interface Consultation {
  id: number
  createTime: number
  updateTime: number
  status: number
  memberName: string
  memberCountry: Country
  memberLang: SupportedLanguage
  memberPhoneNumber: string
  memberPhoneNational: string
  memberPhoneCc: string
  realPhoneNumber4: string
  useVn: boolean
  contactableTime: 'TENTOTWELVE' | 'TWELVETOTHREE' | 'THREETOSIX' | 'ANYTIME'
  consultationProcessStatus: 'PENDING' | 'APPOINTED' | 'COMPLETED' | 'CANCELED'
  reservationTime: string | null
  treatmentExperience: string | null
  reservationTimeList: number[]
  operationTime: Nullable<number>
  lastReservationTime: Nullable<number>
  memberChangeStateTime: Nullable<number>
  hospitalChangeStateTime: Nullable<number>
  rating: Nullable<number>
  ratingCount: Nullable<number>
  reportCount: number
  finishEvalTime: Nullable<number>
  visitedTime: Nullable<number>
  state: keyof typeof ConsultationState // https://www.notion.so/healingpaper/State-8de68900103f4baa994453e305cc0857
  reservationCode: Nullable<string>
  phoneState: Nullable<string> // 'NONE', 'CALLING', 'NOT_CALLED', 'CALLED_NOT_CONNECT', 'CALLED_FAIL', 'CALLED_SUCCESS'
  lastCallTime: Nullable<number>
  lastCallLength: number
  chatState: Nullable<string> // 'INIT', 'ASKED', 'SUCCEEDED', 'FAILED', 'CLOSED', 'ONGOING'
  chatCase: Nullable<string>
  smsCount: number
  means: 'NORMAL' | 'CHAT' | 'PHONE' | Deprecated<'LIVE_CONSULTATION'>
  kakaoId: Nullable<string>
  lineId: Nullable<string>
  memo: string
  adminMemo: Nullable<string>
  report: Nullable<string> // 신고 내용
  reportReason: Nullable<string> // 신고 사유
  originStr:
    | 'NORMAL'
    | 'DIRECT'
    | 'EVENT'
    | 'REVIEW'
    | Deprecated<'REALMODEL'>
    | Deprecated<'LIVE_CONSULTATION'>
    | 'DOCTOR'
    | 'TREATMENT_MENU'

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  origin: OriginType | string
  originId: Nullable<number>
  originMsg: Nullable<string>
  originImage: Nullable<string>
  memberSex: 'MALE' | 'FEMALE' | 'ANY'
  memberEmail: Nullable<string>
  memberAge: number
  memberBirthYear: number
  memberBirthMonth: Nullable<number>
  memberBirthDay: Nullable<number>
  memberSido: Nullable<string>
  memberDeviceType: Nullable<string>
  appVersionCode: Nullable<string>
  visitBooking: Nullable<boolean>
  openCrmDataId: Nullable<string>
  mediaType: Nullable<string>
  mediaOriginId: Nullable<string>
  targetWorked: Nullable<boolean>
  targetPageIds: Nullable<string>
  treatmentMenu: any
  operationType: OperationType
  treatmentIds: string
  specialOfferPrice: number
  specialOfferPriceCurrency: string
  cost: Nullable<{ amount: number; currency: Nullable<string> }>
  pointRedeemedAmount: Nullable<number>
  pointCurrency: Nullable<string>
  targetPageIdList: Nullable<number[]>
  trackingSrc: Nullable<string>
  opTags: string
  operationTags: string
  srcIp: string
  chatRoomId: Nullable<string>
  sameHospitalEstimateId: Nullable<number>
  referrer: Nullable<string>
  motive: Nullable<string>
  etc: Nullable<string>
  photos: Nullable<string>
  estimateId: Nullable<number>
  eventId: number
  realmodelId: Nullable<number>
  reviewId: Nullable<{ reviewId: number; originId: number }>
  tenderId: Nullable<number>
  liveConsultationId: Nullable<number>
  liveConsultationDoctorName: Nullable<string>
  doctorId: Nullable<number>
  vnConsId: Nullable<number>
  operationDoctorId: Nullable<number>
  purchaseId: Nullable<number>
  recentConsultationMemberCount: number
  recentOperatedMemberCount: number
  hasOldEvaluations: boolean
  type: string
  originType: OriginType
  hasAppliedPackage: boolean
  hasEvalProcess: Nullable<any[]>
  canEvalProcess: Nullable<any[]>
  hospitalPointId: number
  isDupCase: boolean
  estimateTitle: string
  eventHotRating: Nullable<number>
  realmodelTitle: string
  reviewTitle: string
  hasVisited: boolean
  hospitalRealNumber: string
  memberChangeState: boolean
  hasToApproveChangedState: boolean
  nextEvalProcess: Nullable<any>
  hospitalId: number
  realPoint: number
  treatmentList: {
    id: number
    name: string
    label: string
  }[]
  memberId: number
  writerId: number
  hospitalName: string
  eventTitle: string
  isAnonymousCons: boolean
  treatmentIdList: number[]
  opTagList: string[]
  canNormalConsultation: boolean
  operationTagList: string[]
  isFromWeb: boolean
  hospitalInfo: {
    id: number
    createTime: Nullable<number>
    updateTime: number
    name: string
    introduction: string
    district: Nullable<string>
    subwayCode: string
    mainImage: string
    profileImage: string
    introImage: string
    photos: {
      id: number
      createTime: number
      updateTime: number
      url: string
      description: string
      seq: number
    }[]
    cardLevel: number
    rating: number
    ratingCount: number
    pageCount: number
    reviewCount: number
    priority: number
    sigunguCode: string
    sido: Nullable<string>
    sigungu: Nullable<string>
    authTenderView: boolean
    authEstimateWrite: boolean
    authChatConsultation: boolean
    authForeignConsultation: boolean
    basicRN: Nullable<string>
    basicVN: string
    officeHours: string
    officeHoursJson: Nullable<string>
    officeHoursEtc: Nullable<string>
    country: Country
    address: string
    addressEn: Nullable<string>
    zipCode: Nullable<string>
    lat: Nullable<number>
    lng: Nullable<number>
    mainTel1: Nullable<string>
    mainTel2: Nullable<string>
    mainTel3: Nullable<string>
    emailList: string[]
    supportingLangList: string[]
    agreedToTermsTime: number
    canChatConsultation: boolean
    isOfficeHour: boolean
    canPhoneConsultation: boolean
    canNormalConsultation: boolean
    treatmentMenuId: Nullable<number>
    recentConsultationMemberCount: number
    recentVisitedMemberCount: number
    recentOperatedMemberCount: number
    entranceType: string
    assessmentState: string
    offlinePointRedemptionEnabled: boolean
    title: string
    treatmentCategoryTagIdList: number[]
    opTagList: string[]
    cardImage: string
    badgeCodeList: string[]
    awardList: string[]
    sidoCode: string
    ratingShow: boolean
    isNew: boolean
    treatmentCategoryTagList: {
      id: number
      name: string
      label: string
      tagMeta: {
        name: string
        nameKr: string
      }
    }[]
    amplitudeTreatmentInfo: {
      treatmentIdList: number[]
      treatmentNameList: string[]
      treatmentCategoryTagIdList: number[]
      treatmentCategoryTagNameList: string[]
      treatmentGroupTagNameList: string[]
      concernBodyPartTagNameList: string[]
      concernTagNameList: string[]
      treatmentLabelList: string[]
      treatmentCategoryTagLabelList: string[]
      treatmentGroupTagLabelList: string[]
      concernBodyPartTagLabelList: string[]
      concernTagLabelList: string[]
      operationType: OperationType
      opTagList: string[]
      operationTagList: string[]
    }
  }
  createdInADay: boolean
  hospitalContactNumber: string
  chat: boolean
  memberPhoneNumberForAdmin: string
  originKr: string
  originName: string
  consProcess: string
  canApplyPackage: boolean
  skipped: boolean
  treatmentCategoryTagList: {
    id: number
    name: string
    label: string
    tagMeta: {
      name: string
      nameKr: string
    }
  }[]
  stateKr: string
  amplitudeTreatmentInfo: {
    treatmentIdList: number[]
    treatmentNameList: string[]
    treatmentCategoryTagIdList: number[]
    treatmentCategoryTagNameList: string[]
    treatmentGroupTagNameList: string[]
    concernBodyPartTagNameList: string[]
    concernTagNameList: string[]
    treatmentLabelList: string[]
    treatmentCategoryTagLabelList: string[]
    treatmentGroupTagLabelList: string[]
    concernBodyPartTagLabelList: string[]
    concernTagLabelList: string[]
    operationType: OperationType
    opTagList: string[]
    operationTagList: string[]
  }
  canChatConsultation: boolean
  canPhoneConsultation: boolean
  consTypeKr: string

  /** @deprecated */
  happyHour: any
  /** @deprecated */
  applyPackageTime: any
}

export interface ConsultationByPurchaseId {
  id: number
  memberId: number
  hospitalId: number
  purchaseId: number
  requestedReservedAt: string
  confirmedReservedAt: string
  originType: OriginType
  originId: number
  originTitle: string
  originImage: string
  specialOfferPrice: number
}

export interface ConsultationHistoryCommand {
  exceptEstimateConsultation: boolean
  hospitalCountry: Country
  length?: number
}

export interface ConsultationHistory {
  consultation: Consultation
  purchase: { id: number; status: string }
}

export interface UpdateConsultationCommand {
  createTime?: number
  updateTime?: number
  status?: number
  memberName?: string
  memberCountry?: Country
  memberLang?: SupportedLanguage
  memberPhoneNumber?: string
  memberPhoneNational?: string
  memberPhoneCc?: string
  realPhoneNumber4?: string
  useVn?: boolean
  contactableTime?: 'TENTOTWELVE' | 'TWELVETOTHREE' | 'THREETOSIX' | 'ANYTIME'
  reservationTime?: string | null
  treatmentExperience?: string | null
  reservationTimeList?: number[]
  operationTime?: Nullable<number>
  lastReservationTime?: Nullable<number>
  memberChangeStateTime?: Nullable<number>
  hospitalChangeStateTime?: Nullable<number>
  rating?: Nullable<number>
  ratingCount?: Nullable<number>
  reportCount?: number
  finishEvalTime?: Nullable<number>
  visitedTime?: Nullable<number>
  state?: keyof typeof ConsultationState // https?://www.notion.so/healingpaper/State-8de68900103f4baa994453e305cc0857
  reservationCode?: Nullable<string>
  phoneState?: Nullable<string> // 'NONE', 'CALLING', 'NOT_CALLED', 'CALLED_NOT_CONNECT', 'CALLED_FAIL', 'CALLED_SUCCESS'
  lastCallTime?: Nullable<number>
  lastCallLength?: number
  chatState?: Nullable<string> // 'INIT', 'ASKED', 'SUCCEEDED', 'FAILED', 'CLOSED', 'ONGOING'
  chatCase?: Nullable<string>
  smsCount?: number
  means?: 'NORMAL' | 'CHAT' | 'PHONE' | Deprecated<'LIVE_CONSULTATION'>
  kakaoId?: Nullable<string>
  lineId?: Nullable<string>
  memo?: string
  adminMemo?: Nullable<string>
  report?: Nullable<string> // 신고 내용
  reportReason?: Nullable<string> // 신고 사유
  originStr?:
    | 'NORMAL'
    | 'DIRECT'
    | 'EVENT'
    | 'REVIEW'
    | Deprecated<'REALMODEL'>
    | Deprecated<'LIVE_CONSULTATION'>
    | 'DOCTOR'
    | 'TREATMENT_MENU'

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  origin?: OriginType | string
  originId?: Nullable<number>
  originMsg?: Nullable<string>
  originImage?: Nullable<string>
  memberSex?: 'MALE' | 'FEMALE' | 'ANY'
  memberEmail?: Nullable<string>
  memberAge?: number
  memberBirthYear?: number
  memberBirthMonth?: Nullable<number>
  memberBirthDay?: Nullable<number>
  memberSido?: Nullable<string>
  memberDeviceType?: Nullable<string>
  appVersionCode?: Nullable<string>
  visitBooking?: Nullable<boolean>
  openCrmDataId?: Nullable<string>
  mediaType?: Nullable<string>
  mediaOriginId?: Nullable<string>
  targetWorked?: Nullable<boolean>
  targetPageIds?: Nullable<string>
  treatmentMenu?: any
  operationType?: OperationType
  treatmentIds?: string
  specialOfferPrice?: number
  specialOfferPriceCurrency?: string
  cost?: Nullable<{ amount?: number; currency?: Nullable<string> }>
  pointRedeemedAmount?: Nullable<number>
  pointCurrency?: Nullable<string>
  targetPageIdList?: Nullable<number[]>
  trackingSrc?: Nullable<string>
  opTags?: string
  operationTags?: string
  srcIp?: string
  chatRoomId?: Nullable<string>
  sameHospitalEstimateId?: Nullable<number>
  referrer?: Nullable<string>
  motive?: Nullable<string>
  etc?: Nullable<string>
  photos?: Nullable<string>
  estimateId?: Nullable<number>
  eventId?: number
  realmodelId?: Nullable<number>
  reviewId?: Nullable<{ reviewId?: number; originId?: number }>
  tenderId?: Nullable<number>
  liveConsultationId?: Nullable<number>
  liveConsultationDoctorName?: Nullable<string>
  doctorId?: Nullable<number>
  vnConsId?: Nullable<number>
  operationDoctorId?: Nullable<number>
  purchaseId?: Nullable<number>
  recentConsultationMemberCount?: number
  recentOperatedMemberCount?: number
  hasOldEvaluations?: boolean
  type?: string
  originType?: OriginType
  hasAppliedPackage?: boolean
  hasEvalProcess?: Nullable<any[]>
  canEvalProcess?: Nullable<any[]>
  hospitalPointId?: number
  isDupCase?: boolean
  estimateTitle?: string
  eventHotRating?: Nullable<number>
  realmodelTitle?: string
  reviewTitle?: string
  hasVisited?: boolean
  hospitalRealNumber?: string
  memberChangeState?: boolean
  hasToApproveChangedState?: boolean
  nextEvalProcess?: Nullable<any>
  hospitalId?: number
  realPoint?: number
  treatmentList?: {
    id?: number
    name?: string
    label?: string
  }[]
  memberId?: number
  writerId?: number
  hospitalName?: string
  eventTitle?: string
  isAnonymousCons?: boolean
  treatmentIdList?: number[]
  opTagList?: string[]
  canNormalConsultation?: boolean
  operationTagList?: string[]
  isFromWeb?: boolean
  hospitalInfo?: {
    id?: number
    createTime?: Nullable<number>
    updateTime?: number
    name?: string
    introduction?: string
    district?: Nullable<string>
    subwayCode?: string
    mainImage?: string
    profileImage?: string
    introImage?: string
    photos?: {
      id?: number
      createTime?: number
      updateTime?: number
      url?: string
      description?: string
      seq?: number
    }[]
    cardLevel?: number
    rating?: number
    ratingCount?: number
    pageCount?: number
    reviewCount?: number
    priority?: number
    sigunguCode?: string
    sido?: Nullable<string>
    sigungu?: Nullable<string>
    authTenderView?: boolean
    authEstimateWrite?: boolean
    authChatConsultation?: boolean
    authForeignConsultation?: boolean
    basicRN?: Nullable<string>
    basicVN?: string
    officeHours?: string
    officeHoursJson?: Nullable<string>
    officeHoursEtc?: Nullable<string>
    country?: Country
    address?: string
    addressEn?: Nullable<string>
    zipCode?: Nullable<string>
    lat?: Nullable<number>
    lng?: Nullable<number>
    mainTel1?: Nullable<string>
    mainTel2?: Nullable<string>
    mainTel3?: Nullable<string>
    emailList?: string[]
    supportingLangList?: string[]
    agreedToTermsTime?: number
    canChatConsultation?: boolean
    isOfficeHour?: boolean
    canPhoneConsultation?: boolean
    canNormalConsultation?: boolean
    treatmentMenuId?: Nullable<number>
    recentConsultationMemberCount?: number
    recentVisitedMemberCount?: number
    recentOperatedMemberCount?: number
    entranceType?: string
    assessmentState?: string
    offlinePointRedemptionEnabled?: boolean
    title?: string
    treatmentCategoryTagIdList?: number[]
    opTagList?: string[]
    cardImage?: string
    badgeCodeList?: string[]
    awardList?: string[]
    sidoCode?: string
    ratingShow?: boolean
    isNew?: boolean
    treatmentCategoryTagList?: {
      id?: number
      name?: string
      label?: string
      tagMeta?: {
        name?: string
        nameKr?: string
      }
    }[]
    amplitudeTreatmentInfo?: {
      treatmentIdList?: number[]
      treatmentNameList?: string[]
      treatmentCategoryTagIdList?: number[]
      treatmentCategoryTagNameList?: string[]
      treatmentGroupTagNameList?: string[]
      concernBodyPartTagNameList?: string[]
      concernTagNameList?: string[]
      treatmentLabelList?: string[]
      treatmentCategoryTagLabelList?: string[]
      treatmentGroupTagLabelList?: string[]
      concernBodyPartTagLabelList?: string[]
      concernTagLabelList?: string[]
      operationType?: OperationType
      opTagList?: string[]
      operationTagList?: string[]
    }
  }
  createdInADay?: boolean
  hospitalContactNumber?: string
  chat?: boolean
  memberPhoneNumberForAdmin?: string
  originKr?: string
  originName?: string
  consProcess?: string
  canApplyPackage?: boolean
  skipped?: boolean
  treatmentCategoryTagList?: {
    id?: number
    name?: string
    label?: string
    tagMeta?: {
      name?: string
      nameKr?: string
    }
  }[]
  stateKr?: string
  amplitudeTreatmentInfo?: {
    treatmentIdList?: number[]
    treatmentNameList?: string[]
    treatmentCategoryTagIdList?: number[]
    treatmentCategoryTagNameList?: string[]
    treatmentGroupTagNameList?: string[]
    concernBodyPartTagNameList?: string[]
    concernTagNameList?: string[]
    treatmentLabelList?: string[]
    treatmentCategoryTagLabelList?: string[]
    treatmentGroupTagLabelList?: string[]
    concernBodyPartTagLabelList?: string[]
    concernTagLabelList?: string[]
    operationType?: OperationType
    opTagList?: string[]
    operationTagList?: string[]
  }
  canChatConsultation?: boolean
  canPhoneConsultation?: boolean
  consTypeKr?: string
}

export interface UpdateChatConsultationCommand extends UpdateConsultationCommand {
  means?: 'CHAT'
  memberName: string
}
